import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ClipboardList, 
  CheckCircle2, 
  AlertTriangle,
  Phone,
  ArrowLeft,
  FileText,
  Users,
  Scale,
  Eye,
  Megaphone,
  BookOpen,
  Shield,
  AlertCircle,
  Clock,
  Siren,
  Wrench,
  MessageSquare,
  BarChart3,
  FolderOpen,
  Search,
  GraduationCap,
  Handshake
} from "lucide-react";

/**
 * 18 תפקידי ממונה בטיחות לפי תקנה 10 לתקנות התשנ"ו-1996
 */
const officerDuties = [
  {
    id: 1,
    icon: MessageSquare,
    title: "ייעוץ למעביד",
    description: "לייעץ למעביד, להנהלת מקום העבודה ולוועדת הבטיחות בכל הנוגע לבטיחות וגיהות"
  },
  {
    id: 2,
    icon: Search,
    title: "איתור מפגעים",
    description: "לאתר מפגעי בטיחות וגיהות במקום העבודה ולהודיע עליהם למעביד"
  },
  {
    id: 3,
    icon: Siren,
    title: "הפסקת עבודה מסוכנת",
    description: "להורות על הפסקת עבודה או תהליך כאשר קיימת סכנה מיידית לחיי אדם או לבריאותו"
  },
  {
    id: 4,
    icon: Scale,
    title: "עמידה בתקנים",
    description: "לפעול לקיום תקנים ישראליים שאימצה מועצת הבטיחות והגיהות התעסוקתית"
  },
  {
    id: 5,
    icon: Users,
    title: "מסירת מידע והדרכה",
    description: "לפעול לקיום חובות מסירת מידע והדרכת עובדים לפי תקנות התשנ\"ט-1999"
  },
  {
    id: 6,
    icon: FileText,
    title: "הכנת תכנית בטיחות",
    description: "להכין ולעדכן תכנית לניהול הבטיחות לפי תקנות התשע\"ג-2013"
  },
  {
    id: 7,
    icon: AlertTriangle,
    title: "בירור סיבות תאונות",
    description: "לברר את הסיבות והנסיבות של תאונות עבודה ומחלות מקצוע ולהמליץ על מניעה"
  },
  {
    id: 8,
    icon: ClipboardList,
    title: "רישום ודיווח תאונות",
    description: "לרשום תאונות עבודה ומחלות מקצוע ולדווח עליהן למפקח העבודה"
  },
  {
    id: 9,
    icon: Wrench,
    title: "הכנת נהלי עבודה",
    description: "להכין נהלי עבודה בטוחים לכל תהליך ופעולה במקום העבודה"
  },
  {
    id: 10,
    icon: Shield,
    title: "פיקוח על ציוד מגן",
    description: "לפקח על אספקה ושימוש נאות בציוד מגן אישי"
  },
  {
    id: 11,
    icon: Eye,
    title: "בדיקות תקופתיות",
    description: "לארגן ולפקח על ביצוע בדיקות תקופתיות למתקנים וציוד"
  },
  {
    id: 12,
    icon: Siren,
    title: "תרגילי חירום",
    description: "לארגן ולקיים תרגילי חירום ופינוי"
  },
  {
    id: 13,
    icon: Megaphone,
    title: "ליווי ביקורות",
    description: "ללוות מפקחי עבודה ובודקים מוסמכים בביקורותיהם"
  },
  {
    id: 14,
    icon: BarChart3,
    title: "דוחות תקופתיים",
    description: "להכין דוחות תקופתיים על מצב הבטיחות והגיהות"
  },
  {
    id: 15,
    icon: FolderOpen,
    title: "ניהול תיק בטיחות",
    description: "לנהל ולעדכן תיק מקום עבודה הכולל את כל מסמכי הבטיחות"
  },
  {
    id: 16,
    icon: Search,
    title: "סקרי סיכונים",
    description: "לערוך סקרי סיכונים ולהמליץ על אמצעי מניעה"
  },
  {
    id: 17,
    icon: GraduationCap,
    title: "עדכון ידע מקצועי",
    description: "להשתתף בהשתלמויות ולעדכן את הידע המקצועי (4 ימים בשנה)"
  },
  {
    id: 18,
    icon: Handshake,
    title: "שיתוף פעולה",
    description: "לשתף פעולה עם נאמני בטיחות, ועדת הבטיחות והעובדים"
  }
];

/**
 * סמכויות ממונה בטיחות לפי סעיף 25 לחוק
 */
const legalAuthorities = [
  {
    category: "סמכויות בחוק",
    items: [
      "כניסה לכל מקום בארגון לצורך ביצוע תפקידו",
      "עיון בכל מסמך הנוגע לבטיחות וגיהות",
      "דרישת מידע מעובדים ומנהלים בנושאי בטיחות",
      "הפסקת עבודה או תהליך בסכנה מיידית",
      "גישה ישירה להנהלה הבכירה בנושאי בטיחות"
    ]
  },
  {
    category: "הגנה על הממונה",
    items: [
      "אין לפגוע בתנאי עבודתו בשל מילוי תפקידו",
      "הגנה על הממונה מפיטורים בשל דיווחים",
      "עצמאות מקצועית בקבלת החלטות בטיחות"
    ]
  },
  {
    category: "חובות דיווח",
    items: [
      "דיווח מיידי על תאונות חמורות למפקח עבודה",
      "דיווח על תאונות רגילות תוך 4 ימי עבודה",
      "הכנת דוחות תקופתיים להנהלה",
      "תיעוד כל ההדרכות והאירועים"
    ]
  }
];

const dailyActivities = [
  { time: "בוקר", activity: "סיור בטיחות יומי באתר העבודה", icon: Eye },
  { time: "בוקר", activity: "בדיקת ציוד מגן אישי וזמינותו", icon: Shield },
  { time: "צהריים", activity: "ביצוע הדרכות לעובדים חדשים", icon: Users },
  { time: "צהריים", activity: "טיפול בדוחות ותיעוד", icon: FileText },
  { time: "אחה\"צ", activity: "מעקב אחר תיקון ליקויים", icon: Wrench },
  { time: "אחה\"צ", activity: "פגישות עם מנהלים ועדכונים", icon: MessageSquare }
];

const qualifications = [
  {
    title: "הסמכה רשמית",
    description: "תעודת ממונה בטיחות מוכרת ממשרד העבודה (תקנה 5)",
    icon: GraduationCap
  },
  {
    title: "ניסיון בענף",
    description: "2-3 שנות ניסיון לפחות בענף הרלוונטי (תעשייה/בנייה)",
    icon: Clock
  },
  {
    title: "השתלמות שנתית",
    description: "4 ימי השתלמות בשנה לעדכון ידע מקצועי (תקנה 6)",
    icon: BookOpen
  },
  {
    title: "ידע מקצועי",
    description: "היכרות מעמיקה עם חקיקה, תקנים ופרקטיקות בטיחות",
    icon: Scale
  },
  {
    title: "כישורים אישיים",
    description: "יכולת תקשורת, סמכותיות ויחסי אנוש טובים",
    icon: Handshake
  }
];

export default function SafetyOfficerRoles() {
  return (
    <Layout>
      <Helmet>
        <title>18 תפקידי ממונה בטיחות לפי החוק | Adam Safety</title>
        <meta name="description" content="רשימת 18 תפקידי ממונה בטיחות לפי תקנה 10 לתקנות התשנו-1996 - סמכויות, חובות משפטיות ופעילות יומיומית." />
        <link rel="canonical" href="https://adam-safety.com/services/safety-officer-roles" />
        <meta property="og:title" content="18 תפקידי ממונה בטיחות לפי החוק | Adam Safety" />
        <meta property="og:description" content="רשימת 18 תפקידי ממונה בטיחות לפי תקנה 10 לתקנות התשנו-1996 - סמכויות, חובות משפטיות ופעילות יומיומית." />
        <meta property="og:url" content="https://adam-safety.com/services/safety-officer-roles" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="18 תפקידי ממונה בטיחות לפי החוק | Adam Safety" />
        <meta name="twitter:description" content="רשימת 18 תפקידי ממונה בטיחות לפי תקנה 10 לתקנות התשנו-1996 - סמכויות, חובות משפטיות ופעילות יומיומית." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <ClipboardList className="w-5 h-5" />
              <span className="text-sm font-medium">תקנה 10 לתקנות התשנ"ו</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              18 תפקידי ממונה בטיחות
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              רשימת התפקידים המלאה לפי החוק - 
              סמכויות, חובות משפטיות ופעילות יומיומית
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  התייעצות עם מומחה
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 18 Duties Grid */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              18 התפקידים לפי תקנה 10
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              רשימת התפקידים המחייבת לפי תקנות ממונים על הבטיחות התשנ"ו-1996
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {officerDuties.map((duty, index) => (
              <motion.div
                key={duty.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-glass p-5"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-accent">{duty.id}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <duty.icon className="w-4 h-4 text-accent" />
                      <h3 className="font-bold text-foreground">{duty.title}</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">{duty.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 max-w-4xl mx-auto"
          >
            <div className="bg-muted/50 rounded-xl p-4 text-sm text-muted-foreground">
              <p className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>מקור:</strong> תקנה 10 לתקנות ארגון הפיקוח על העבודה (ממונים על הבטיחות), התשנ&quot;ו-1996.
                  רשימה זו מהווה את החובות המינימליות של ממונה בטיחות.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Legal Authorities */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              סמכויות משפטיות (סעיף 25 לחוק)
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              ממונה בטיחות נושא באחריות משפטית ובעל סמכויות מוגדרות בחוק
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {legalAuthorities.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-3xl p-8 text-primary-foreground"
              >
                <div className="flex items-center gap-2 mb-6">
                  <Scale className="w-6 h-6 text-accent" />
                  <h3 className="text-xl font-bold">{category.category}</h3>
                </div>
                <ul className="space-y-3">
                  {category.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-primary-foreground/90">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Daily Activities */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              יום בחייו של ממונה בטיחות
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              דוגמה לפעילות יומיומית טיפוסית
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {dailyActivities.map((activity, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 card-glass p-4"
                >
                  <div className={`w-20 text-center py-2 rounded-lg ${
                    activity.time === 'בוקר' 
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : activity.time === 'צהריים'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                  }`}>
                    <span className="text-sm font-medium">{activity.time}</span>
                  </div>
                  <activity.icon className="w-5 h-5 text-accent" />
                  <p className="text-foreground">{activity.activity}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              כשירות ודרישות (תקנה 5)
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              מה נדרש כדי לשמש כממונה בטיחות
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {qualifications.map((qual, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <qual.icon className="w-5 h-5 text-accent" />
                  <h3 className="font-bold text-foreground">{qual.title}</h3>
                </div>
                <p className="text-muted-foreground text-sm">{qual.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              צריכים ממונה בטיחות מקצועי?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              צוות הממונים שלנו מביא את כל הכישורים והניסיון הנדרשים
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  דברו איתנו
                </Button>
              </Link>
              <Link to="/services/external-consulting">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  שירותי ממונה חיצוני
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
